import { useState, useEffect, useCallback, useRef } from "react";

// Hook debounce interno
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

// LocalStorage Helpers
const loadStorageConfig = (key) => {
  if (!key) return null;
  try {
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : null;
  } catch (e) {
    console.log(e);
    return null;
  }
};

const saveStorageConfig = (key, config) => {
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(config));
  } catch (e) {
    console.log(e);
  }
};

export function useDatatable(props) {
  const {
    apiUrl,
    requestDataFn,
    columns,
    rowIdField = "id",
    defaultSortBy,
    defaultSortDir,
    initialSortBy,
    initialSortDir,
    maxPerPage = 10000,
    initialPerPage = 10,
    orderColumns = true,
    rows: rowsProp = [],
    dataPagination,
    onPageChange,
    onPerPageChange,
    onSortChange,
    onColumnFilterChange,
    onGlobalSearchChange,
    forceLoading = false,
    storageKey, // NEW
  } = props;

  // --- CONFIGURACIÓN DE COLUMNAS ---
  const mergeColumnsWithStorage = useCallback((baseColumns) => {
    const baseCols = (baseColumns || []).map(col => {
      return {
        ...col,
        searchable: col.searchForColumn === undefined ? col.searchable : !!col.searchForColumn,
        isVisible: true
      };
    });

    const saved = loadStorageConfig(storageKey);
    let mergedCols = baseCols;
    let widthsObj = null;

    if (saved) {
      if (saved.cols) {
        const savedColMap = new Map(saved.cols.map(c => [c.key, c]));
        const baseColMap = new Map(baseCols.map(c => [c.key, c]));
        mergedCols = [];

        // Add columns in saved order if they still exist in baseCols
        saved.cols.forEach(sc => {
          if (baseColMap.has(sc.key)) {
            mergedCols.push({ ...baseColMap.get(sc.key), isVisible: sc.isVisible !== false });
          }
        });

        // Append new columns that were not in saved config
        baseCols.forEach(bc => {
          if (!savedColMap.has(bc.key)) {
            mergedCols.push({ ...bc, isVisible: true });
          }
        });
      }
      widthsObj = saved.colWidthsObj;
    }


    const mergedWidths = mergedCols.map(col => {
      if (widthsObj?.[col.key]) return widthsObj[col.key];
      const colAncho = col.minWidth ? col.minWidth : '150px';
      return col.width ? col.width : colAncho;
    });

    return { mergedCols, mergedWidths };
  }, [storageKey]);

  const [cols, setCols] = useState(() => mergeColumnsWithStorage(columns).mergedCols);
  const [colWidths, setColWidths] = useState(() => mergeColumnsWithStorage(columns).mergedWidths);

  useEffect(() => {
    const { mergedCols, mergedWidths } = mergeColumnsWithStorage(columns);
    setCols(mergedCols);
    setColWidths(mergedWidths);
  }, [columns, mergeColumnsWithStorage]);

  // --- GUARDAR EN LOCALSTORAGE ---
  useEffect(() => {
    if (!storageKey) return;

    // Convert colWidths array to a mapping by col.key
    const colWidthsObj = {};
    cols.forEach((col, idx) => {
      colWidthsObj[col.key] = colWidths[idx];
    });

    const configToSave = {
      cols: cols.map(c => ({ key: c.key, isVisible: c.isVisible !== false })),
      colWidthsObj,
    };

    saveStorageConfig(storageKey, configToSave);
  }, [storageKey, cols, colWidths]);

  // Toggling column visibility
  const toggleColumnVisibility = useCallback((colKey) => {
    setCols(prevCols => prevCols.map(c =>
      c.key === colKey ? { ...c, isVisible: !c.isVisible } : c
    ));
  }, []);

  // Reset layout to defaults
  const resetLayout = useCallback(() => {
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
    const baseCols = (columns || []).map(col => {
      return {
        ...col,
        searchable: col.searchForColumn === undefined ? col.searchable : !!col.searchForColumn,
        isVisible: true
      };
    });

    const rtnAncho = obj => obj.minWidth ? obj.minWidth : '150px';
    const baseWidths = baseCols.map(col => col.width ? col.width : rtnAncho(col));

    setCols(baseCols);
    setColWidths(baseWidths);
  }, [storageKey, columns]);

  const [draggingKey, setDraggingKey] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [dragOverColIdx, setDragOverColIdx] = useState(null);

  const [inputHoverColIdx, setInputHoverColIdx] = useState(null);

  // --- ESTADO DE DATOS Y PAGINACIÓN ---
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [internalPage, setInternalPage] = useState(1);
  const [internalPerPage, setInternalPerPage] = useState(initialPerPage);
  const [internalTotal, setInternalTotal] = useState(0);

  const [sortBy, setSortBy] = useState(initialSortBy || defaultSortBy || '');
  const [sortDir, setSortDir] = useState(initialSortDir || defaultSortDir || '');
  const [globalSearch, setGlobalSearch] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Set());

  const debouncedGlobalSearch = useDebounce(globalSearch, 800);
  const debouncedColumnFilters = useDebounce(columnFilters, 800);
  const debouncedSortBy = useDebounce(sortBy, 800);
  const debouncedSortDir = useDebounce(sortDir, 800);
  const [manualInputPage, setManualInputPage] = useState();

  // --- LÓGICA HÍBRIDA ---
  const isControlled = !!dataPagination;
  const effectivePage = isControlled ? dataPagination.current_page : internalPage;
  const effectivePerPage = isControlled ? dataPagination.per_page : internalPerPage;
  const effectiveTotal = isControlled ? dataPagination.total : internalTotal;
  const effectiveLastPage = dataPagination?.last_page ?? (Math.ceil(effectiveTotal / effectivePerPage) || 1);
  const totalPages = effectiveLastPage;

  const debouncedEffectivePage = useDebounce(effectivePage, 600);

  const isDebouncing =
    effectivePage !== debouncedEffectivePage ||
    globalSearch !== debouncedGlobalSearch ||
    columnFilters !== debouncedColumnFilters ||
    sortBy !== debouncedSortBy ||
    sortDir !== debouncedSortDir;

  const currentFetchDeps = JSON.stringify([apiUrl, debouncedEffectivePage, effectivePerPage, debouncedSortBy, debouncedSortDir, debouncedGlobalSearch, debouncedColumnFilters]);
  const lastFetchDepsRef = useRef(null);

  const isPendingFetch = !!(requestDataFn || apiUrl) && lastFetchDepsRef.current !== currentFetchDeps;

  const loadingState = forceLoading || isLoading || isDebouncing || isPendingFetch;

  const condicionalFunc = (fetchIdRef, fetchId, resultData, resultTotal) => {
    if (fetchIdRef.current === fetchId) {
      setRows(resultData);
      setInternalTotal(resultTotal);
      setIsLoading(false);
    }
  }

  // --- FETCH CENTRALIZADO ---
  const fetchIdRef = useRef(0);
  const fetchData = useCallback(async () => {
    if (!requestDataFn && !apiUrl) return;
    lastFetchDepsRef.current = currentFetchDeps;
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);
    try {
      const safePerPage = effectivePerPage > maxPerPage ? maxPerPage : effectivePerPage;
      const params = {
        page: debouncedEffectivePage,
        perPage: safePerPage,
        sortBy: debouncedSortBy,
        sortDir: debouncedSortDir,
        search: debouncedGlobalSearch.trim(),
        filters: debouncedColumnFilters
      };
      let resultData = [];
      let resultTotal = 0;
      if (typeof requestDataFn === 'function') {
        const response = await requestDataFn(params);
        resultData = response.data || [];
        resultTotal = response.total || 0;
      } else if (apiUrl) {
        const urlParams = new URLSearchParams();
        urlParams.set("page", String(params.page));
        urlParams.set("per_page", String(params.perPage));
        if (params.sortBy && params.sortDir) {
          urlParams.set("sort_by", params.sortBy);
          urlParams.set("sort_dir", params.sortDir);
        }
        if (params.search) urlParams.set("search", params.search);
        Object.entries(params.filters).forEach(([key, value]) => {
          if (value.trim()) urlParams.set(`filter[${key}]`, value.trim());
        });
        const resp = await fetch(`${apiUrl}?${urlParams.toString()}`);
        if (!resp.ok) throw new Error(`Error ${resp.status}`);
        const json = await resp.json();
        resultData = json.data || [];
        resultTotal = json.total || 0;
      }
      condicionalFunc(fetchIdRef, fetchId, resultData, resultTotal);
      
    } catch (error) {
      console.error("Datatable fetch error:", error);
      condicionalFunc(fetchIdRef, fetchId, [], 0);
    }
  }, [apiUrl, requestDataFn, currentFetchDeps, effectivePerPage, maxPerPage, debouncedSortBy, debouncedSortDir, debouncedGlobalSearch, debouncedColumnFilters]);

  useEffect(() => {
    if (requestDataFn || apiUrl) {
      fetchData();
    }
  }, [fetchData]);

  useEffect(() => {
    if (!apiUrl && !requestDataFn && Array.isArray(rowsProp)) {
      setRows(rowsProp);
      setInternalTotal(rowsProp.length);
    }
  }, [apiUrl, requestDataFn, rowsProp]);

  // --- HANDLERS ---
  const handleSortClick = (columnKey, direction) => {
    if (!columnKey) return;
    let newDirection = direction;
    if (!direction) {
      newDirection = (sortBy === columnKey && sortDir === "asc") ? "desc" : "asc";
    }
    setSortBy(columnKey);
    setSortDir(newDirection);
    if (typeof onSortChange === 'function') onSortChange(columnKey, newDirection);
  };

  const handleGlobalSearchChange = (e) => {
    setGlobalSearch(e.target.value);
    if (!isControlled) setInternalPage(1);
  };

  const handleGlobalSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (typeof onGlobalSearchChange === 'function') onGlobalSearchChange(globalSearch);
    }
  };

  const handleColumnFilterChange = (columnKey, value) => {
    setColumnFilters(prev => ({ ...prev, [columnKey]: value }));
    if (!isControlled) setInternalPage(1);
    if (typeof onColumnFilterChange === 'function') onColumnFilterChange(columnKey, value);
  };

  const handlePageChange = (nextPage) => {
    setInternalPage(nextPage);
  };

  const handlePerPageChange = (e) => {
    const value = Number(e.target.value) || 10;
    const safe = value > maxPerPage ? maxPerPage : value;
    setManualInputPage(undefined);
    if (isControlled && typeof onPerPageChange === 'function') {
      onPerPageChange(safe);
      if (typeof onPageChange === 'function') onPageChange(1);
    } else {
      setInternalPerPage(safe);
      setInternalPage(1);
    }
  };

  const toggleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = rows.map(r => r[rowIdField]);
      setSelectedRows(new Set(allIds));
    } else {
      setSelectedRows(new Set());
    }
  };

  const toggleSelectRow = (id) => {
    setSelectedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleClearFilters = () => {
    setGlobalSearch("");
    setColumnFilters({});
    setSortBy(initialSortBy || '');
    setSortDir(initialSortDir || '');
    if (!isControlled) setInternalPage(1);
    if (typeof onGlobalSearchChange === 'function') onGlobalSearchChange("");
    if (typeof onSortChange === 'function') onSortChange(initialSortBy || '', initialSortDir || '');
  };

  // Drag & drop logic
  const handleDragStart = (e, key) => {
    if (orderColumns) {
      setDraggingKey(key);
      const img = new Image();
      img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
      e.dataTransfer.setDragImage(img, 0, 0);
    }
  };

  const handleDragOver = (e, key, colIdx) => {
    if (!orderColumns || !draggingKey || draggingKey === key) return;
    e.preventDefault();

    // Usamos el bounding rect del <th> para evitar el flickering
    // Solo intercambiaremos cuando el mouse cruce la mitad de la columna objetivo
    const fromIdx = cols.findIndex(c => c.key === draggingKey);
    const toIdx = cols.findIndex(c => c.key === key);

    if (fromIdx !== -1 && toIdx !== -1) {
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX;

      // Hysteresis: buffer para no intercambiar inmediatamente al tocar el borde
      // Si movemos a la derecha, solo intercambiamos si el mouse pasó la mitad del objetivo
      if (fromIdx < toIdx && mouseX < rect.left + rect.width / 2) return;
      // Si movemos a la izquierda, solo intercambiamos si el mouse pasó la otra mitad
      if (fromIdx > toIdx && mouseX > rect.right - rect.width / 2) return;

      const newCols = [...cols];
      const newColWidths = [...colWidths];

      const [movedCol] = newCols.splice(fromIdx, 1);
      newCols.splice(toIdx, 0, movedCol);

      const [movedWidth] = newColWidths.splice(fromIdx, 1);
      newColWidths.splice(toIdx, 0, movedWidth);

      setCols(newCols);
      setColWidths(newColWidths);
    }
  };

  const handleDragLeave = (e, key) => {
    if (!orderColumns) return;
    setDragOverKey(null);
    setDragOverColIdx(null);
  };

  const handleDrop = (e, targetKey) => {
    if (!orderColumns || !draggingKey) return;
    e.preventDefault();

    setDraggingKey(null);
    setDragOverKey(null);
    setDragOverColIdx(null);
  };

  return {
    cols,
    setCols,
    colWidths,
    setColWidths,
    toggleColumnVisibility,
    resetLayout,
    draggingKey,
    setDraggingKey,
    dragOverKey,
    setDragOverKey,
    dragOverColIdx,
    setDragOverColIdx,
    inputHoverColIdx,
    setInputHoverColIdx,
    rows,
    setRows,
    isLoading,
    setIsLoading,
    internalPage,
    setInternalPage,
    internalPerPage,
    setInternalPerPage,
    internalTotal,
    setInternalTotal,
    sortBy,
    setSortBy,
    sortDir,
    setSortDir,
    globalSearch,
    setGlobalSearch,
    columnFilters,
    setColumnFilters,
    selectedRows,
    setSelectedRows,
    manualInputPage,
    setManualInputPage,
    isControlled,
    effectivePage,
    effectivePerPage,
    effectiveTotal,
    effectiveLastPage,
    totalPages,
    loadingState,
    handleSortClick,
    handleGlobalSearchChange,
    handleGlobalSearchKeyDown,
    handleColumnFilterChange,
    handlePageChange,
    handlePerPageChange,
    toggleSelectAll,
    toggleSelectRow,
    handleClearFilters,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop
  };
}
